import { TextField, TextFieldProps } from '@mui/material';
import { DesktopDatePicker, DesktopDatePickerProps } from '@mui/x-date-pickers';
import {
    format, isAfter, isBefore, isFuture, isPast, isValid
} from 'date-fns';
import { useEffect, useState } from 'react';


export type ValidatedDateFieldProps = Omit<TextFieldProps, 'onChange'> & {
    pickerProps?: Partial<DesktopDatePickerProps<Date, Date>>;
    onValidDateChange: (date: Date | null) => void;
};

interface DateFieldValue {
    date: Date | null; // TODO fix the | null here so that it can work with a required date as well
    inputValue: string | undefined;
}

export function ValidatedDateField({
    pickerProps, onValidDateChange, value, ...textFieldProps
}: ValidatedDateFieldProps) {
    const [ { date, inputValue }, setValue ] = useState<DateFieldValue>({
        date: value as Date | null || null,
        inputValue: undefined
    });

    useEffect(() => {
        setValue(({ inputValue }) => ({
            date: value as Date | null, // TODO enforce value type here
            inputValue
        }));
    }, [ value ]);

    const errorMessage = validatePickerDate(pickerProps, date, inputValue);

    useEffect(() => {
        if (!errorMessage) {
            onValidDateChange(date);
        }
    }, [
        date, errorMessage, onValidDateChange
    ]);

    const [ showError, setShowError ] = useState(false);

    return (
        <DesktopDatePicker<Date>
            {...pickerProps}
            value={date}
            inputFormat="MM/dd/yyyy"
            onChange={(date, inputValue) => setValue({
                date,
                inputValue
            })}
            renderInput={params => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    onBlur={() => setShowError(true)}
                    onFocus={() => {
                        if (!errorMessage) {
                            setShowError(false);
                        }
                    }}
                    error={showError && !!errorMessage}
                    helperText={showError && (errorMessage || textFieldProps.helperText)}
                    inputRef={params.inputRef}
                    inputProps={{
                        ...textFieldProps.inputProps,
                        ...params.inputProps
                    }}
                    InputProps={{
                        ...textFieldProps.InputProps,
                        ...params.InputProps
                    }}
                />
            )}
        />
    );
}

function validatePickerDate(
    pickerProps: ValidatedDateFieldProps['pickerProps'],
    date: Date | null,
    inputValue: string | undefined
) {
    const {
        minDate,
        maxDate,
        disableFuture,
        disablePast,
        shouldDisableDate
    } = pickerProps || {};

    if (!date) {
        return;
    }

    if (!!inputValue && inputValue.length > 0 && inputValue.length !== 10) {
        return 'Date is incomplete';
    }

    if (!isValid(date) || shouldDisableDate?.(date)) {
        return 'Date is invalid';
    }

    if (disableFuture && isFuture(date)) {
        return 'Date cannot be in the future';
    }

    if (disablePast && isPast(date)) {
        return 'Date cannot be in the future';
    }

    if (maxDate && isAfter(date, maxDate)) {
        return `Date cannot be after ${format(maxDate, 'MM/dd/yyyy')}`;
    }

    if (minDate && isBefore(date, minDate)) {
        return `Date cannot be before ${format(minDate, 'MM/dd/yyyy')}`;
    }
}
